import { useEffect } from "react";

export const SITE_NAME = "看料88";
export const SITE_ORIGIN = "https://aspve.cn";

type SeoOptions = {
  title: string;
  description: string;
  keywords?: string;
  path: string;
  type?: "website" | "article";
  image?: string;
  robots?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  if (!content) return;
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute("data-seo", "managed");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute("data-seo", "managed");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function useSeo(options: SeoOptions) {
  const {
    title,
    description,
    keywords,
    path,
    type = "website",
    image = `${SITE_ORIGIN}/og-cover.jpg`,
    robots = "index,follow,max-image-preview:large",
    jsonLd,
  } = options;

  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    const url = `${SITE_ORIGIN}${path === "/" ? "" : path}`;

    document.title = title;
    document.documentElement.lang = "zh-CN";

    upsertMeta("name", "description", description);
    if (keywords) upsertMeta("name", "keywords", keywords);
    upsertMeta("name", "robots", robots);

    upsertLink("canonical", url);

    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:locale", "zh_CN");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    const scriptId = "seo-jsonld";
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    if (jsonLdKey) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.type = "application/ld+json";
      script.textContent = jsonLdKey;
      document.head.appendChild(script);
    }
  }, [title, description, keywords, path, type, image, robots, jsonLdKey]);
}

export default useSeo;